import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useAppAuth } from '@/contexts/app-auth.context'
import { Button } from '@/components/ui/button'
import { Heading, Text } from '@/components/ui/typography'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import {
  directoryApiClient,
  type Staff,
  type RoleWithMembers,
  type ScheduleEntry,
} from '@/lib/api/directory.client'
import { directoryAdminApiClient } from '@/lib/api/directory-admin.client'
import { scheduleEntryMatchesAt } from '@super/shared-types'
import { formatRelativeTime } from '../formatters'
import {
  formatScheduleFallback,
  formatScheduleWhen,
  partitionTimeOff,
} from './helpers'
import ScheduleEntryDialog from './ScheduleEntryDialog'

export default function TimeOffSection(props: {
  schedule: ScheduleEntry[]
  roles: RoleWithMembers[]
  staff: Staff[]
  companyId: string
  onChange: () => void
}) {
  const { schedule, roles, staff, companyId, onChange } = props
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<ScheduleEntry | null>(null)

  const timeOff = useMemo(
    () => schedule.filter(entry => entry.kind === 'TIME_OFF'),
    [schedule],
  )

  return (
    <div className="mt-8">
      <TimeOffTable
        entries={timeOff}
        roles={roles}
        staff={staff}
        companyId={companyId}
        onAdd={() => setCreating(true)}
        onEdit={entry => setEditing(entry)}
        onChange={onChange}
      />

      {creating && (
        <ScheduleEntryDialog
          companyId={companyId}
          kind="TIME_OFF"
          roles={roles}
          staff={staff}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            onChange()
          }}
        />
      )}
      {editing && (
        <ScheduleEntryDialog
          companyId={companyId}
          kind="TIME_OFF"
          roles={roles}
          staff={staff}
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            onChange()
          }}
        />
      )}
    </div>
  )
}

function TimeOffTable(props: {
  entries: ScheduleEntry[]
  roles: RoleWithMembers[]
  staff: Staff[]
  companyId: string
  onAdd: () => void
  onEdit: (entry: ScheduleEntry) => void
  onChange: () => void
}) {
  const { entries, roles, staff, companyId, onAdd, onEdit, onChange } = props
  const { isSuperAdmin } = useAppAuth()
  const [showPast, setShowPast] = useState(false)
  const roleById = useMemo(
    () => Object.fromEntries(roles.map(role => [role.id, role])),
    [roles],
  )
  const staffById = useMemo(
    () => Object.fromEntries(staff.map(member => [member.id, member])),
    [staff],
  )

  const remove = async (id: string, label: string) => {
    if (isSuperAdmin) {
      await directoryAdminApiClient.deleteScheduleEntry(companyId, id)
    } else {
      await directoryApiClient.deleteScheduleEntry(id)
    }
    toast.success(`Removed time off${label ? ` for ${label}` : ''}`)
    onChange()
  }

  const { current, past } = useMemo(
    () => partitionTimeOff(entries, new Date()),
    [entries],
  )

  const renderRow = (entry: ScheduleEntry, isPast: boolean) => {
    const role = entry.roleId ? roleById[entry.roleId] : null
    const stf = entry.staffId ? staffById[entry.staffId] : null
    const matchesNow =
      !isPast &&
      scheduleEntryMatchesAt(entry, new Date(), {
        checkDateWindow: true,
      })
    const fallback = formatScheduleFallback(entry, { staffById, roleById })
    return (
      <tr
        key={entry.id}
        className={cn('border-t', isPast && 'text-muted-foreground')}
      >
        <td className="px-4 py-3">
          <Badge variant={role ? 'secondary' : 'outline'}>
            {role ? 'Role' : 'Staff'}
          </Badge>
        </td>
        <td className="px-4 py-3">{role?.name ?? stf?.name ?? '(missing)'}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span>{formatScheduleWhen(entry)}</span>
            {matchesNow && (
              <Badge
                variant="secondary"
                className="bg-amber-100 text-amber-700 hover:bg-amber-100"
              >
                Off now
              </Badge>
            )}
          </div>
        </td>
        <td className={cn('px-4 py-3', !fallback && 'text-muted-foreground')}>
          {fallback || '—'}
        </td>
        <td className="px-4 py-3 text-muted-foreground" title={entry.updatedAt}>
          {formatRelativeTime(entry.updatedAt)}
        </td>
        <td className="px-4 py-3 text-right">
          <RowActions
            onEdit={() => onEdit(entry)}
            onRemove={() => remove(entry.id, role?.name ?? stf?.name ?? '')}
          />
        </td>
      </tr>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <Heading variant="subsection-title">
            Time off ({current.length})
          </Heading>
          <Text variant="muted" className="text-sm">
            Override availability — vacations, sick days, off-hours. Time off
            wins over any availability entry for the same person.
          </Text>
        </div>
        <Button onClick={onAdd}>Add time off</Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-left px-4 py-3 font-medium">Who</th>
              <th className="text-left px-4 py-3 font-medium">When</th>
              <th className="text-left px-4 py-3 font-medium">Fallback</th>
              <th className="text-left px-4 py-3 font-medium">Updated</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {current.map(entry => renderRow(entry, false))}
            {current.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  No time off today or later.
                </td>
              </tr>
            )}
            {past.length > 0 && (
              <>
                <tr className="border-t bg-muted/30">
                  <td colSpan={6} className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => setShowPast(previous => !previous)}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      {showPast ? 'Hide' : 'Show'} entries that ended before
                      today ({past.length})
                    </button>
                  </td>
                </tr>
                {showPast && past.map(entry => renderRow(entry, true))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function RowActions(props: { onEdit: () => void; onRemove: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuItem onClick={props.onEdit}>
          <Pencil className="w-4 h-4 mr-2" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={props.onRemove}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Remove
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
